import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useBlocker, useNavigate } from 'react-router'

import { isEmpty } from 'lodash-es'
import { bindActionCreators } from 'redux'

import {
  SAVE_CONFIRMATION_BODY_TEXT,
  SAVE_CONFIRMATION_BUTTON_TEXT,
  SAVE_CONFIRMATION_TITLE,
  UNSAVED_CHANGES_BODY_TEXT,
  UNSAVED_CHANGES_CANCEL_TEXT,
  UNSAVED_CHANGES_CONFIRMATION_TEXT,
  UNSAVED_CHANGES_TITLE,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { useInterval } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useInterval'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  BookingMode,
  BuiltEvent,
  CALENDAR_CONFIGURATION_ADMIN_STATUS,
  CALENDAR_PROVIDER_ADMIN_STATUS,
  EventModality,
  ProviderCalendarConfiguration,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { ProviderCalendarLoadingModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerCalendarLoadingModal/ProviderCalendarLoadingModal'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { ProviderBookableCalendarSetup } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/providerBookableCalendarSetup/ProviderBookableCalendarSetup'

import '../settings/providerBookableCalendar.scss'

import {
  useGetAvailableCalendarsQuery,
  useGetCalendarAvailabilitySlotsQuery,
  useGetCalendarProviderQuery,
  useGetCalendarsQuery,
  usePatchCalendarConfigurationMutation,
  usePatchCalendarProviderMutation,
  usePostCalendarEventsTemplateMutation,
  usePutAvailableCalendarsMutation,
  usePutCalendarEventsTemplateCancelMutation,
} from './data/calendarApi'
import { showCalendarLiveModal } from './data/calendarSlice'
import { getBookableMaxSlots, getBookableTarget, getSlotsToUpdate, mapEventToBookableEvent } from './utils'
import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import { LC_CALENDAR_CURRENT, SETTINGS } from '../common/constants/routingConstants'
import {
  getAuthConfig,
  getAuthEmploymentStatus,
  getAuthInPersonPreference,
  getAuthIsLTSupervisor,
  getAuthUserId,
  getAuthVideoPreference,
} from '../data/auth/authSelectors'
import { setSelectedTimeZone } from '../data/authActions'
import { useAppDispatch } from '../data/store'
import { useCalendarConfiguration } from './data/hooks/useCalendarConfiguration'
import { useTimeZone } from './hooks/useTimeZone'
import { setToastContent } from '../lyraTherapy/data/ltToastAutoActions'
import { getProviderDetailsAddresses, getProviderDetailsData } from '../providers/data/providerSelectors'
import { getProvider } from '../providers/individualProvider/data/providerDetailsActions'
import { useGetVideoSessionPreferenceQuery } from '../settings/settingsApi'

const CHECK_RECURRING_EVENTS_INTERVAL_MS = 5000
const MAX_CHECK_RECURRING_EVENTS_ATTEMPTS = 2

const CalendarSetup: FunctionComponent<CalendarSetupProps> = ({ isEdit = false, actions: { getProvider } }) => {
  const {
    isInProductCalendarEnabled,
    isInProductCalendarModalityEnabled,
    isExpandableWorkingHoursEnabled,
    isDAEngageOnOffPlatformVideoEnabled,
    isTimezoneSelectorEnabled,
  } = useFlags()
  const config = useSelector(getAuthConfig)
  const navigate = useNavigate()
  const userId = useSelector(getAuthUserId)
  const isSupervisor = useSelector(getAuthIsLTSupervisor)
  const inPersonPreference = useSelector(getAuthInPersonPreference) ?? false
  const videoPreference = useSelector(getAuthVideoPreference) ?? true
  const providerDetailsData = useSelector(getProviderDetailsData)
  const providerAddresses = useSelector(getProviderDetailsAddresses)
  const shouldShowModalitySection =
    isInProductCalendarModalityEnabled && !!config?.inProductCalendar?.showModalitySelection
  const { data: configurations } = useGetCalendarsQuery({ providerId: userId }, { refetchOnMountOrArgChange: true })
  const { data: availableCalendars } = useGetAvailableCalendarsQuery(
    { providerId: userId },
    { refetchOnMountOrArgChange: true },
  )
  const timeZone = useTimeZone()
  const [isSaving, setIsSaving] = useState(false)
  const employmentStatus = useSelector(getAuthEmploymentStatus)
  const [showMaxModal, setShowMaxModal] = useState(false)
  const [dirty, setIsDirty] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [shouldPollForRecurringEventsAdded, setShouldPollForRecurringEventsAdded] = useState(false)
  const [eventsToSave, setEventsToSave] = useState<BuiltEvent[] | null>(null)
  const [slotMinTimeToSave, setSlotMinTimeToSave] = useState<string | undefined>(undefined)
  const [slotMaxTimeToSave, setSlotMaxTimeToSave] = useState<string | undefined>(undefined)
  const checkRecurringEventsAddedAttempts = useRef(0)
  const canNavigateBack = useRef(false)

  const calendarConfiguration = useCalendarConfiguration()
  const { data: calendarProvider } = useGetCalendarProviderQuery({ providerId: userId })
  const { data: videoSessionPreference } = useGetVideoSessionPreferenceQuery(
    { id: userId },
    { skip: !isDAEngageOnOffPlatformVideoEnabled || !userId || !config?.supportsOnOffPlatformVideo },
  )
  const defaultVideoPlatform = videoSessionPreference ?? null
  const { data: availabilitySlots, refetch: refetchAvailabilitySlots } = useGetCalendarAvailabilitySlotsQuery(
    {
      providerId: userId,
    },
    { refetchOnMountOrArgChange: true },
  )

  const [postCalendarEventsTemplate] = usePostCalendarEventsTemplateMutation()
  const [patchCalendarProvider] = usePatchCalendarProviderMutation()
  const [putCalendarEventsTemplateCancel] = usePutCalendarEventsTemplateCancelMutation()
  const [patchCalendarConfiguration] = usePatchCalendarConfigurationMutation()
  const [putAvailableCalendars] = usePutAvailableCalendarsMutation()
  useFetcher([getProvider, [userId], isEmpty(providerDetailsData) && shouldShowModalitySection])
  const dispatch = useAppDispatch()
  const initialEvents = useMemo(() => {
    return (
      availabilitySlots?.map((slot) => ({
        startTime: slot.start_datetimetz,
        endTime: slot.end_datetimetz,
        id: slot.external_id,
        title: slot.title,
        modality: slot.extended_properties?.private?.modality_availability
          ? (slot.extended_properties?.private?.modality_availability as EventModality)
          : undefined,
        booking_type: slot.booking_type,
      })) ?? []
    )
  }, [availabilitySlots])

  const handleTimeZoneChange = useCallback((tz: string) => dispatch(setSelectedTimeZone(tz)), [dispatch])

  const target =
    (employmentStatus
      ? config?.inProductCalendar?.bookableTarget?.[employmentStatus]
      : getBookableTarget(employmentStatus)) ?? getBookableTarget(employmentStatus)

  const maxSlots =
    (employmentStatus
      ? config?.inProductCalendar?.bookableMax?.[employmentStatus]
      : getBookableMaxSlots(employmentStatus)) ?? getBookableMaxSlots(employmentStatus)

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault()
        event.returnValue = true
      }
    },
    [dirty],
  )

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnload)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [beforeUnload])

  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }): boolean => {
    if (!canNavigateBack.current && dirty && currentLocation.pathname !== nextLocation.pathname) {
      setShowUnsavedChangesModal(true)
      track({
        event: isEdit
          ? mixpanelEvents.CALENDAR_TEMPLATE_CANCEL_MODAL_SHOWN
          : mixpanelEvents.CALENDAR_SETUP_CANCEL_MODAL_SHOWN,
      })
      return true
    } else {
      return false
    }
  })

  const navigateBack = useCallback(
    (isSuccess: boolean = false) => {
      canNavigateBack.current = true
      if (isEdit) {
        navigate(SETTINGS.route)
      } else {
        if (isSuccess) {
          dispatch(showCalendarLiveModal())
        }
        navigate(LC_CALENDAR_CURRENT.route)
      }
    },
    [dispatch, isEdit, navigate],
  )

  const handleError = useCallback(() => {
    if (isEdit) {
      dispatch(
        setToastContent({
          text: 'Failed to update calendar',
          toastType: 'error',
          id: 'CalendarSetup-failure',
        }),
      )
    } else {
      dispatch(
        setToastContent({
          text: 'Failed to create calendar',
          toastType: 'error',
          id: 'CalendarSetup-failure',
        }),
      )
    }

    navigateBack()
  }, [dispatch, isEdit, navigateBack])

  const handleSuccess = useCallback(() => {
    if (isEdit) {
      dispatch(
        setToastContent({
          text: config?.inProductCalendar?.hybridBookingMode
            ? 'Availability updated'
            : 'Updated. Clients will now be able to book these times',
          toastType: 'success',
          id: 'CalendarSetup-success',
        }),
      )
    }
    navigateBack(true)
  }, [isEdit, navigateBack, dispatch, config?.inProductCalendar?.hybridBookingMode])

  const modifyCalendarPayload = useCallback((calendars: ProviderCalendarConfiguration[]) => {
    return calendars.map((calendar) => {
      return {
        ...calendar,
        admin_status: CALENDAR_CONFIGURATION_ADMIN_STATUS.PUBLISHED,
      }
    })
  }, [])

  const updateBookableTemplate = useCallback(
    ({
      events,
      calendarConfigurationId,
      slotMinTime,
      slotMaxTime,
    }: {
      events: BuiltEvent[] | null
      calendarConfigurationId: string
      slotMinTime: string | undefined
      slotMaxTime: string | undefined
    }) => {
      if (!userId) {
        return
      }
      setIsSaving(true)

      const promises: Promise<any>[] = []
      if (!isEdit) {
        promises.push(
          patchCalendarProvider({
            providerId: userId,
            body: {
              booking_mode: config?.inProductCalendar?.hybridBookingMode ? BookingMode.HYBRID : BookingMode.BOOKABLE,
              admin_status: CALENDAR_PROVIDER_ADMIN_STATUS.ACTIVE,
            },
          }).unwrap(),
        )
        if (!config?.inProductCalendar?.hybridBookingMode) {
          promises.push(
            patchCalendarConfiguration({
              configurationId: calendarConfigurationId,
              body: { admin_status: CALENDAR_CONFIGURATION_ADMIN_STATUS.PUBLISHED },
            }).unwrap(),
          )
        }
      }

      if (
        isExpandableWorkingHoursEnabled &&
        config?.inProductCalendar?.showExpandableWorkingHours &&
        (slotMinTime || slotMaxTime)
      ) {
        promises.push(
          patchCalendarProvider({
            providerId: userId,
            body: {
              daily_start: slotMinTime,
              daily_end: slotMaxTime,
            },
          }).unwrap(),
        )
      }

      if (events) {
        const { slotsToAdd, slotsToRemove } = getSlotsToUpdate(events, initialEvents, timeZone)
        const eventsToCreate = slotsToAdd.map((event) =>
          mapEventToBookableEvent(event, timeZone, true, config?.inProductCalendar?.hybridBookingMode),
        )
        const eventIdsToRemove = slotsToRemove.map((event) => event.id)
        if (eventsToCreate.length > 0) {
          promises.push(
            postCalendarEventsTemplate({
              provider_calendar_configuration_id: calendarConfigurationId,
              events: eventsToCreate,
            }).unwrap(),
          )
        }

        if (eventIdsToRemove.length > 0) {
          promises.push(
            putCalendarEventsTemplateCancel({
              provider_calendar_configuration_id: calendarConfigurationId,
              event_ids: eventIdsToRemove,
            }).unwrap(),
          )
        }

        if (
          config?.inProductCalendar?.hybridBookingMode &&
          configurations != null &&
          configurations?.length > 0 &&
          availableCalendars != null &&
          availableCalendars?.length > 0
        ) {
          const configurationIds = configurations.map((config) => config.id)
          promises.push(
            putAvailableCalendars({
              provider_id: userId,
              configurations: modifyCalendarPayload(
                availableCalendars.filter((config) => configurationIds.includes(config.id)),
              ),
            }),
          )
        }
      }

      Promise.all(promises)
        .then(() => {
          setIsSaving(false)
          handleSuccess()
        })
        .catch((error) => {
          if (!isEdit && error.response?.status === 504) {
            setShouldPollForRecurringEventsAdded(true)
          } else {
            setIsSaving(false)
            handleError()
          }
        })
    },
    [
      userId,
      isEdit,
      isExpandableWorkingHoursEnabled,
      config?.inProductCalendar?.showExpandableWorkingHours,
      config?.inProductCalendar?.hybridBookingMode,
      patchCalendarProvider,
      patchCalendarConfiguration,
      initialEvents,
      timeZone,
      configurations,
      availableCalendars,
      postCalendarEventsTemplate,
      putCalendarEventsTemplateCancel,
      putAvailableCalendars,
      modifyCalendarPayload,
      handleSuccess,
      handleError,
    ],
  )

  const onSave = useCallback(
    (events: BuiltEvent[], slotMinTime: string, slotMaxTime: string) => {
      if (!calendarConfiguration) {
        return
      }
      // setCurrentSlotsAmount(events.length)

      // if (events.length < target) {
      //   track({
      //     event: mixpanelEvents.BUTTON_PRESS,
      //     action: isEdit ? mixpanelActions.CALENDAR_TEMPLATE_SAVE_BELOW : mixpanelActions.CALENDAR_SETUP_SAVE_BELOW,
      //   })
      //   setShowMinModal(true)
      //   return
      // }

      if (events.length > maxSlots && !config?.hidePerformanceMetrics) {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: isEdit ? mixpanelActions.CALENDAR_TEMPLATE_SAVE_ABOVE : mixpanelActions.CALENDAR_SETUP_SAVE_ABOVE,
        })
        setShowMaxModal(true)
        return
      }

      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: isEdit ? mixpanelActions.CALENDAR_TEMPLATE_SAVE_SUCCESS : mixpanelActions.CALENDAR_SETUP_SAVE_SUCCESS,
      })
      setSlotMinTimeToSave(slotMinTime)
      setSlotMaxTimeToSave(slotMaxTime)
      setEventsToSave(events)
    },
    [calendarConfiguration, config, isEdit, maxSlots],
  )

  const onSaveConfirm = useCallback(() => {
    if ((eventsToSave || slotMinTimeToSave || slotMaxTimeToSave) && calendarConfiguration?.id) {
      if (slotMinTimeToSave || slotMaxTimeToSave) {
        track({
          event: mixpanelEvents.CALENDAR_TEMPLATE_WORKING_HOURS_UPDATED,
          action: mixpanelActions.CALENDAR_TEMPLATE_SAVE_WORKING_HOURS,
          details: {
            startHour: slotMinTimeToSave,
            endHour: slotMaxTimeToSave,
          },
        })
      }
      updateBookableTemplate({
        events: eventsToSave,
        calendarConfigurationId: calendarConfiguration.id,
        slotMinTime: slotMinTimeToSave,
        slotMaxTime: slotMaxTimeToSave,
      })
    }

    setEventsToSave(null)
  }, [calendarConfiguration, eventsToSave, slotMaxTimeToSave, slotMinTimeToSave, updateBookableTemplate])

  const onCalendarChanged = useCallback(
    (events: BuiltEvent[]) => {
      const { slotsToAdd, slotsToRemove } = getSlotsToUpdate(events, initialEvents, timeZone)
      setIsDirty(slotsToAdd.length > 0 || slotsToRemove.length > 0)
    },
    [initialEvents, timeZone],
  )

  const onCancel = useCallback(() => {
    if (config?.inProductCalendar?.subCalendarSelection && !isEdit) {
      navigate(LC_CALENDAR_CURRENT.route)
    } else {
      navigate(-1)
    }
  }, [navigate, config, isEdit])

  const checkRecurringEventsAdded = useCallback(async () => {
    if (checkRecurringEventsAddedAttempts.current >= MAX_CHECK_RECURRING_EVENTS_ATTEMPTS || !userId) {
      handleError()
      return
    }
    checkRecurringEventsAddedAttempts.current++
    const slots = await refetchAvailabilitySlots().unwrap()
    if (slots.length > 0) {
      handleSuccess()
    }
  }, [handleError, handleSuccess, refetchAvailabilitySlots, userId])
  useInterval(checkRecurringEventsAdded, shouldPollForRecurringEventsAdded ? CHECK_RECURRING_EVENTS_INTERVAL_MS : null)
  if (!isInProductCalendarEnabled) {
    return null
  }

  if (initialEvents == null) {
    return <LoadingIndicator />
  }

  // const amountToOpen = target - (currentSlotsAmount ?? 0)
  return (
    <>
      <div className='lc-bookable-calendar'>
        <ProviderBookableCalendarSetup
          onCancel={onCancel}
          onSave={onSave}
          timeZone={timeZone}
          onTimeZoneChange={
            isTimezoneSelectorEnabled && isSupervisor && config?.inProductCalendar?.showTimezoneSelector
              ? handleTimeZoneChange
              : undefined
          }
          isSaving={isSaving}
          initialEvents={initialEvents}
          isEditing={isEdit}
          bookableTarget={target}
          bookableMax={maxSlots}
          onChange={onCalendarChanged}
          hidePerformanceMetrics={config?.hidePerformanceMetrics}
          hybridBookingMode={config?.inProductCalendar?.hybridBookingMode}
          calendarProvider={calendarProvider}
          showExpandableWorkingHours={config?.inProductCalendar?.showExpandableWorkingHours}
          flexibleAvailabilityWindows={config?.inProductCalendar?.flexibleAvailabilityWindows}
          defaultVideoPlatform={defaultVideoPlatform}
          inPersonPreference={inPersonPreference}
          splitVideoOptions={isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo}
          providerAddresses={providerAddresses}
          showModalitySelection={shouldShowModalitySection}
          supportsOnOffPlatformVideo={!!config?.supportsOnOffPlatformVideo}
          videoPreference={videoPreference}
          isNPSession={config?.inProductCalendar?.showNpSessionSelection}
          expandedEndTime={config?.inProductCalendar?.expandedEndTime ?? config?.inProductCalendar?.dailyEnd}
          expandedStartTime={config?.inProductCalendar?.expandedStartTime ?? config?.inProductCalendar?.dailyStart}
          displayStartTime={isSupervisor && isTimezoneSelectorEnabled ? '00:00:00' : undefined}
          displayEndTime={isSupervisor && isTimezoneSelectorEnabled ? '24:00:00' : undefined}
          hiddenDays={config?.inProductCalendar?.hiddenDays}
        />
      </div>
      <ConfirmationModal
        visible={showUnsavedChangesModal}
        onRequestClose={() => {
          navigationBlocker.reset?.()
          setShowUnsavedChangesModal(false)
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: isEdit
              ? mixpanelActions.CALENDAR_TEMPLATE_CANCEL_MODAL_CONTINUE_EDITING
              : mixpanelActions.CALENDAR_SETUP_CANCEL_MODAL_CONTINUE_EDITING,
          })
        }}
        onConfirmationButtonPress={() => {
          navigationBlocker.reset?.()
          setShowUnsavedChangesModal(false)
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: isEdit
              ? mixpanelActions.CALENDAR_TEMPLATE_CANCEL_MODAL_CONTINUE_EDITING
              : mixpanelActions.CALENDAR_SETUP_CANCEL_MODAL_CONTINUE_EDITING,
          })
        }}
        onCancelPressed={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: isEdit
              ? mixpanelActions.CALENDAR_TEMPLATE_CANCEL_MODAL_DISCARD_CHANGES
              : mixpanelActions.CALENDAR_SETUP_CANCEL_MODAL_DISCARD_CHANGES,
          })
          if (navigationBlocker.state === 'blocked') {
            navigationBlocker.proceed()
          } else {
            onCancel()
          }
        }}
        modalTitle={
          <FormattedMessage
            {...(config?.inProductCalendar?.hybridBookingMode && !isEdit
              ? UNSAVED_CHANGES_TITLE.HYBRID_SETUP
              : UNSAVED_CHANGES_TITLE.DEFAULT)}
          />
        }
        cancelButtonText={
          <FormattedMessage
            {...(config?.inProductCalendar?.hybridBookingMode && !isEdit
              ? UNSAVED_CHANGES_CANCEL_TEXT.HYBRID_SETUP
              : UNSAVED_CHANGES_CANCEL_TEXT.DEFAULT)}
          />
        }
        confirmationButtonText={
          <FormattedMessage
            {...(config?.inProductCalendar?.hybridBookingMode && !isEdit
              ? UNSAVED_CHANGES_CONFIRMATION_TEXT.HYBRID_SETUP
              : UNSAVED_CHANGES_CONFIRMATION_TEXT.DEFAULT)}
          />
        }
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                {...(config?.inProductCalendar?.hybridBookingMode
                  ? isEdit
                    ? UNSAVED_CHANGES_BODY_TEXT.HYBRID_EDIT
                    : UNSAVED_CHANGES_BODY_TEXT.HYBRID_SETUP
                  : isEdit
                    ? UNSAVED_CHANGES_BODY_TEXT.BOOKABLE_EDIT
                    : UNSAVED_CHANGES_BODY_TEXT.BOOKABLE_SETUP)}
              />
            }
          />
        }
        testID={'UnsavedChangesWarningModal'}
      />
      <ConfirmationModal
        visible={eventsToSave != null}
        onRequestClose={() => setEventsToSave(null)}
        onConfirmationButtonPress={onSaveConfirm}
        modalTitle={
          <FormattedMessage
            {...(config?.inProductCalendar?.hybridBookingMode
              ? isEdit
                ? SAVE_CONFIRMATION_TITLE.HYBRID_EDIT
                : SAVE_CONFIRMATION_TITLE.HYBRID_SETUP
              : SAVE_CONFIRMATION_TITLE.BOOKABLE)}
          />
        }
        cancelButtonText={<FormattedMessage defaultMessage='Go back' description='Cancel button' />}
        confirmationButtonText={
          <FormattedMessage
            {...(config?.inProductCalendar?.hybridBookingMode && !isEdit
              ? SAVE_CONFIRMATION_BUTTON_TEXT.HYBRID_SETUP
              : SAVE_CONFIRMATION_BUTTON_TEXT.DEFAULT)}
          />
        }
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                {...(isEdit
                  ? SAVE_CONFIRMATION_BODY_TEXT.EDIT
                  : config?.inProductCalendar?.hybridBookingMode
                    ? SAVE_CONFIRMATION_BODY_TEXT.HYBRID_SETUP
                    : SAVE_CONFIRMATION_BODY_TEXT.BOOKABLE_SETUP)}
              />
            }
          />
        }
        testID={'SaveCalendarEventsConfirmationModal'}
      />
      {/* <ConfirmationModal
        visible={showMinModal}
        onRequestClose={() => setShowMinModal(false)}
        onConfirmationButtonPress={() => setShowMinModal(false)}
        modalTitle={
          <FormattedMessage
            defaultMessage='Add {amountToOpen, plural,
              one {# more bookable spot}
              other {# more bookable spots}}'
            description='Title of modal where provider needs to open more slots'
            values={{
              amountToOpen,
            }}
          />
        }
        showCancel={false}
        confirmationButtonText={<FormattedMessage defaultMessage='Go back' description='Go back to modify template' />}
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='You need between {target}-{maxSlots} bookable spots per standard working week for this template to be saved.'
                description='Body of modal where provider needs to open more slots'
                values={{
                  target,
                  maxSlots,
                }}
              />
            }
          />
        }
      /> */}
      <ConfirmationModal
        visible={showMaxModal}
        onRequestClose={() => setShowMaxModal(false)}
        onConfirmationButtonPress={() => setShowMaxModal(false)}
        modalTitle={
          <FormattedMessage
            defaultMessage='Remove excess calendar spots'
            description='Title of modal where provider has too many slots'
          />
        }
        showCancel={false}
        confirmationButtonText={<FormattedMessage defaultMessage='Go back' description='Go back to modify template' />}
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='You need between {target}-{maxSlots} calendar spots per standard working week for this template to be saved.'
                description='Body of modal where provider has too many slots'
                values={{
                  target,
                  maxSlots,
                }}
              />
            }
          />
        }
        testID={'ExcessCalendarSpotsModal'}
      />
      <ProviderCalendarLoadingModal isVisible={isSaving} />
    </>
  )
}

type CalendarSetupProps = {
  isEdit?: boolean
  actions: {
    getProvider: (params: Parameters<typeof getProvider>[0]) => Promise<any>
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      getProvider: getProvider as any,
    },
    dispatch,
  ),
})

const connector = connect(null, mapDispatchToProps)

export default connector(CalendarSetup)
